<template>
  <div id="vessel-map" class="vs-con-loading__container">
    <div id="vesselVoyageHistoryMap">
      <div class="player">
        <vessel-voyage-history-player-control
          v-if="startTime"
          :start="startTime"
          :current="currentTime"
          :end="endTime"
          :fps="fps"
          :is_pause="isPause"
          @setFPS="setFPS"
          @onPlay="onPlay"
          @onPause="onPause"
          @onSliderDown="onSliderDown"
          @onSliderUp="onSliderUp"
          @onClickPrev="onClickPrev"
          @onClickNext="onClickNext"
        />
      </div>
      <div id="vessel-history-camera-are" v-show="cameraList.length > 0">
        <vs-row vs-align="flex-start">
          <vs-col
            vs-type="flex"
            vs-justify="center"
            vs-align="center"
            :vs-w="cameraVsw"
          >
            <div
              class="history-camera-component"
              :key="index"
              v-for="(camera, index) in historyCamerasRow1"
            >
              <hls-video-player
                :camera="camera"
                :start="startTime"
                :current="currentTime"
                :end="endTime"
                :fps="fps"
                :isPause="isPause"
                :width="300"
                @isCameraReady="isCameraReady"
              />
            </div>
          </vs-col>
          <vs-col
            vs-type="flex"
            vs-justify="center"
            vs-align="center"
            :vs-w="cameraVsw"
          >
            <div
              class="history-camera-component"
              :key="index"
              v-for="(camera, index) in historyCamerasRow2"
            >
              <hls-video-player
                :camera="camera"
                :start="startTime"
                :current="currentTime"
                :end="endTime"
                :fps="fps"
                :isPause="isPause"
                :width="300"
                @isCameraReady="isCameraReady"
              />
            </div>
          </vs-col>
        </vs-row>
      </div>
    </div>
    <div class="mapboxgl-ctrl-bottom-right">
      <div id="vessel-style-change" class="mapboxgl-ctrl">
        <map-style-changer :map="map"></map-style-changer>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { mapGetters } from "vuex";
import Mapbox from "mapbox-gl";
import moment from "moment-timezone";
import { VoyageHistoryMapLayer, VoyageHistoryMapSource } from "@/config/Map.js";
import VesselVoyageHistoryPlayerControl from "./VesselVoyageHistoryPlayerControl";
import MapStyleChanger from "./MapStyleChanger";
import LocalDbService from "@/services/localDbService";
import HlsVideoPlayer from "./HlsVideoPlayer";
import * as VoyageHistoryUtil from "@/utils/VoyageHistoryUtil";
import * as turf from "@turf/turf";

// Mixins
import { LocalizeMixin, LayerMixin } from "@/mixins";
import * as VesselUtil from "@/utils/VesselUtil";

// Store
const {
  mapActions: mapActionsOfEnterpriseVesselVoyageHistory,
} = createNamespacedHelpers("enterpriseVesselVoyageHistory");

const { mapState: mapStateOfVesselView } = createNamespacedHelpers(
  "enterpriseVesselView"
);

export default {
  name: "VesselVoyageHistoryMap",
  mixins: [LocalizeMixin, LayerMixin],
  components: {
    VesselVoyageHistoryPlayerControl,
    MapStyleChanger,
    HlsVideoPlayer
  },
  data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      center: [139.86448000000001, 35.54817666666667],
      zoom: 13,
      mapStyleLocal: null,
      isStyleChange: false,
      unSubscribeStore: null,
      historyCamerasRow1: [],
      historyCamerasRow2: [],
      cameraVsw: "12",
      displayTimeLimits: -10
    };
  },
  props: {
    vessel_id: { type: String, required: false },
    vessel_name: { type: String, required: false },
    start_time: { type: String, required: false },
    end_time: { type: String, required: false }
  },
  computed: {
    ...mapGetters({
      currentTime: "enterpriseVesselVoyageHistory/getCurrentTime",
      myWakes: "enterpriseVesselVoyageHistory/getMyWakes",
      startTime: "enterpriseVesselVoyageHistory/getStartTime",
      endTime: "enterpriseVesselVoyageHistory/getEndTime",
      myPoints: "enterpriseVesselVoyageHistory/getMyPoints",
      fps: "enterpriseVesselVoyageHistory/getFPS",
      isPause: "enterpriseVesselVoyageHistory/getPause",
      cameraList: "enterpriseVesselVoyageHistory/getCameraList",
      vesselsArray: "enterpriseVesselVoyageHistory/getVesselMapObjects",
      myTracks: "enterpriseVesselVoyageHistory/getMyTracks",
      startPointGeoJson: "enterpriseVesselVoyageHistory/getStartPointGeoJson"
    }),
    ...mapStateOfVesselView({ mapStyle: state => state.mapStyle })
  },
  watch: {
    mapStyle(val) {
      this.isStyleChange = true;
      this.map.setStyle(val);
      this.mapStyleLocal = val;
    },
    currentTime() {
      this.drawVessels(this.currentTime);
      this.drawTrack(this.currentTime);
    }
  },
  async mounted() {
    await this.initMap();

    this.$vs.loading({
      container: "#vessel-map",
      text: "航海履歴データを読み込み中..."
    });

    this.map.on("load", async () => {
      await this.addSources();
      await this.addLayers();

      let hasVesselHistory = await this.getMyVesselHistory({
        vesselId: this.vessel_id,
        startTime: this.start_time,
        endTime: this.end_time
      });

      if (hasVesselHistory && this.myTracks()) {
        await this.getOtherVesselsHistory({ myVesselId: this.vessel_id });
        this.initPlayer();

        await this.fetchCameraList(this.vessel_id);
        await this.setHistoryCameras();

        await this.setStartPointVessels();
        this.drawStartPointVessels();

        this.$vs.loading.close("#vessel-map > .con-vs-loading");

        let myTracks = this.myTracks();
        this.map
          .getSource(VoyageHistoryMapSource.VoyageHistoryVesselWakeLineSource.id)
          .setData(myTracks);
        let startPoint = myTracks.features[0].geometry.coordinates[0];
        this.map.setCenter(startPoint);
      } else {
        this.$vs.loading.close("#vessel-map > .con-vs-loading");
        setTimeout(() => {
          this.openNoVoyageHistoryAlert();
        }, 1000);
      }
    });

    this.unSubscribeStore = this.$store.subscribe((mutation, _) => {
      if (mutation.type === "TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE") {
        if (this.map && !this.doMapResize) {
          this.doMapResize = true;
          setTimeout(() => {
            this.map.resize();
            this.doMapResize = false;
          }, 1000);
        }
      }
    });

    this.map.on("style.load", async () => {
      if (this.isStyleChange) {
        this.$vs.loading({
          container: "#vessel-map",
          text: "地図スタイルを変更中..."
        });
        await this.addSources();
        await this.addLayers();
        let myTracks = this.myTracks();
        if (myTracks) {
          this.map
            .getSource(
              VoyageHistoryMapSource.VoyageHistoryVesselWakeLineSource.id
            )
            .setData(myTracks);
        }
        this.drawVessels(this.currentTime);
        this.drawTrack(this.currentTime);
        this.isStyleChange = false;
        setTimeout(() => {
          this.$vs.loading.close("#vessel-map > .con-vs-loading");
        }, 2000);
      }
    });
  },
  beforeDestroy() {
    this.resetVoyageHistoryState();
    this.$store.dispatch("enterpriseVoyageHistory/clear");
    if (this.unSubscribeStore) {
      this.unSubscribeStore();
    }
  },
  methods: {
    ...mapActionsOfEnterpriseVesselVoyageHistory([
      "resetVoyageHistoryState",
      "getMyVesselHistory",
      "getOtherVesselsHistory",
      "setStartPointVessels",
      "initPlayer",
      "fetchCameraList"
    ]),

    getWorkerCount() {
      return navigator.hardwareConcurrency / 2 + 1;
    },

    async initMap() {
      this.mapStyleLocal = await LocalDbService.getMapStyle();
      Mapbox.prewarm();
      Mapbox.workerCount = this.getWorkerCount();
      Mapbox.maxParallelImageRequests = 10;
      Mapbox.accessToken = this.accessToken;
      this.map = new Mapbox.Map({
        container: "vesselVoyageHistoryMap",
        style: this.mapStyleLocal,
        center: this.center,
        zoom: this.zoom
      });
      let nav = new Mapbox.NavigationControl();
      this.map.addControl(nav, "bottom-right");
      this.map.on("style.load", this.setMapLanguage);
    },

    drawVessels(trackProgress) {
      if (!trackProgress) return;

      if (trackProgress === this.startTime) {
        this.drawStartPointVessels();
        this.drawTrack();
        return;
      }

      let vesselsArray = this.vesselsArray;
      if (!vesselsArray) return;

      let pointFeatures = [];
      let polygonFeatures = [];
      let myVesselCoordinates = [];
      Object.keys(vesselsArray).forEach(key => {
        const vessel = vesselsArray[key];
        const pointsArray = vessel.pointsArray;
        if (!pointsArray) return;
        const cursor = this.getCurrentPointIndex(pointsArray, trackProgress);
        if (cursor <= 0) return;

        const currentPoint = pointsArray[cursor];

        if (pointsArray.length - 1 === cursor) {
          let dateTo = moment.unix(trackProgress);
          let dateFrom = moment.unix(currentPoint.epoch);
          let gapMin = dateFrom.diff(dateTo, "minutes");
          if (this.displayTimeLimits >= gapMin) return;
        }

        if (vessel.isMyVessel) {
          myVesselCoordinates = [currentPoint.lon, currentPoint.lat];
        }

        const geometry = {
          type: "Point",
          coordinates: [currentPoint.lon, currentPoint.lat]
        };

        let timeDiff = pointsArray[cursor].epoch - pointsArray[cursor - 1].epoch;
        const properties = {
          vesselName: vessel.vesselName,
          mmsi: vessel.mmsi,
          icon: VesselUtil.getSymbolIconNameLightWeight(
            Number(vessel.vesselType),
            pointsArray[cursor].speed,
            timeDiff
          ),
          speed: pointsArray[cursor].speed,
          heading: currentPoint.heading,
          hasSize: vessel.hasSize
        };
        pointFeatures.push(turf.feature(geometry, properties));

        if (vessel.hasSize) {
          let actualVessel = VoyageHistoryUtil.createPolygonFeature(
            vessel.vesselId,
            {
              lon: currentPoint.lon,
              lat: currentPoint.lat,
              h: currentPoint.heading
            },
            vessel.polygonData
          );
          polygonFeatures = polygonFeatures.concat(actualVessel);
        }
      });

      let polygonCollection = turf.featureCollection(polygonFeatures);
      this.map
        .getSource(VoyageHistoryMapSource.VoyageHistoryVesselPolygonSource.id)
        .setData(polygonCollection);
      let pointCollection = turf.featureCollection(pointFeatures);
      this.map
        .getSource(VoyageHistoryMapSource.VoyageHistoryVesselSymbolSource.id)
        .setData(pointCollection);

      if (myVesselCoordinates.length > 0) {
        this.map.setCenter(myVesselCoordinates);
      }
    },

    drawTrack(trackProgress) {
      if (!trackProgress) return;
      const filters = ["<=", ["number", ["get", "epoch"]], trackProgress];
      this.map.setFilter(
        VoyageHistoryMapLayer.VoyageHistoryVesselWakeLineLayer.id,
        filters
      );
    },

    drawStartPointVessels() {
      let startPointGeoJson = this.startPointGeoJson();
      if (startPointGeoJson) {
        this.map
          .getSource(VoyageHistoryMapSource.VoyageHistoryVesselPolygonSource.id)
          .setData(startPointGeoJson.polygon);
        this.map
          .getSource(VoyageHistoryMapSource.VoyageHistoryVesselSymbolSource.id)
          .setData(startPointGeoJson.point);
      }
    },

    getCurrentPointIndex(pointsArray, trackProgress) {
      for (const [index, value] of pointsArray.entries()) {
        if (value.epoch >= trackProgress) {
          return index;
        }
      }
      return pointsArray.length - 1;
    },

    setMapLanguage() {
      if (this.isDefaultMapStyle(this.mapStyleLocal)) {
        let lang = this.getBrowserLanguage();
        let local = "name_" + lang;
        if (this.map) {
          let textLayerIds = this.getTextLayerIds();
          textLayerIds.forEach(id => {
            this.map.setLayoutProperty(id, "text-field", ["get", local]);
          });
        }
      }
    },

    async addSources() {
      this.map.addSource(
        VoyageHistoryMapSource.VoyageHistoryVesselSymbolSource.id,
        {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: []
          }
        }
      );
      this.map.addSource(
        VoyageHistoryMapSource.VoyageHistoryVesselPolygonSource.id,
        {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: []
          }
        }
      );
      this.map.addSource(
        VoyageHistoryMapSource.VoyageHistoryVesselWakeLineSource.id,
        {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: []
          }
        }
      );
    },

    async addLayers() {
      this.map.addLayer({
        id: VoyageHistoryMapLayer.VoyageHistoryVesselWakeLineLayer.id,
        type: "line",
        source: VoyageHistoryMapSource.VoyageHistoryVesselWakeLineSource.id,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1]
        }
      });

      this.map.addLayer({
        id: VoyageHistoryMapLayer.VoyageHistoryVesselSymbolLayer.id,
        type: "symbol",
        source: VoyageHistoryMapSource.VoyageHistoryVesselSymbolSource.id,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading"
          }
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "opacity"
          }
        }
      });

      this.map.addLayer({
        id: VoyageHistoryMapLayer.VoyageHistoryVesselPolygonLayer.id,
        type: "fill",
        source: VoyageHistoryMapSource.VoyageHistoryVesselPolygonSource.id,
        maxzoom: 24,
        minzoom: 14,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co"
          }
        }
      });

      this.map.addLayer({
        id: VoyageHistoryMapLayer.VoyageHistoryVesselWithoutSizeLayer.id,
        type: "symbol",
        source: VoyageHistoryMapSource.VoyageHistoryVesselSymbolSource.id,
        minzoom: 14,
        filter: ["==", "hasSize", false],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading"
          }
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "opacity"
          }
        }
      });

      this.map.addLayer({
        id: VoyageHistoryMapLayer.VoyageHistoryVesselTextLayer.id,
        type: "symbol",
        source: VoyageHistoryMapSource.VoyageHistoryVesselSymbolSource.id,
        maxzoom: 24,
        minzoom: 10,
        layout: {
          "text-field": ["get", "vesselName"],
          "text-anchor": "top",
          "text-size": 14,
          "text-max-width": 30,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
          "text-allow-overlap": true,
          "text-ignore-placement": true
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });
    },

    // VesselVoyageHistoryPlayerControl - setFPS
    setFPS(value) {
      this.$store.dispatch("enterpriseVesselVoyageHistory/setFPS", value);
    },
    // VesselVoyageHistoryPlayerControl - onPlay
    onPlay() {
      this.$store.dispatch("enterpriseVesselVoyageHistory/playTicker", true);
    },
    // VesselVoyageHistoryPlayerControl - onPause
    onPause() {
      this.$store.dispatch("enterpriseVesselVoyageHistory/playTicker", false);
    },
    // VesselVoyageHistoryPlayerControl - onSliderDown
    onSliderDown() {
      this.$store.dispatch("enterpriseVesselVoyageHistory/playTicker", false);
    },
    // VesselVoyageHistoryPlayerControl - onSliderUp
    onSliderUp(value) {
      this.$store.dispatch(
        "enterpriseVesselVoyageHistory/setCurrentTime",
        value
      );
      this.$store.dispatch("enterpriseVesselVoyageHistory/playTicker", true);
    },
    isCameraReady() {
      this.$vs.loading.close("#vessel-map > .con-vs-loading");
    },
    onClickPrev() {
      this.$emit("onClickPrev");
    },
    onClickNext() {
      this.$emit("onClickNext");
    },
    async setHistoryCameras() {
      switch (this.cameraList.length) {
        case 1:
          this.historyCamerasRow1 = this.cameraList;
          break;
        case 2:
          this.historyCamerasRow1 = this.cameraList;
          break;
        case 3:
          this.cameraVsw = 6;
          this.historyCamerasRow1.push(this.cameraList[0]);
          this.historyCamerasRow1.push(this.cameraList[1]);
          this.historyCamerasRow2.push(this.cameraList[2]);
          break;
        case 4:
          this.cameraVsw = 6;
          this.historyCamerasRow1.push(this.cameraList[0]);
          this.historyCamerasRow1.push(this.cameraList[1]);
          this.historyCamerasRow2.push(this.cameraList[2]);
          this.historyCamerasRow2.push(this.cameraList[3]);
          break;
      }
    },

    openNoVoyageHistoryAlert() {
      let startLocal = moment(this.start_time)
        .local()
        .format("YYYY年MM月DD日 HH:mm");
      let endLocal = moment(this.end_time)
        .local()
        .format("HH:mm");
      this.$vs.dialog({
        color: "danger",
        title: `航海履歴が見つかりません`,
        text: `${this.vessel_name}(${startLocal}~${endLocal})の航海履歴が見つかりませんでした。他の時間帯を選択してください。`,
        acceptText: "OK"
      });
    }
  }
};
</script>

<style lang="scss">
#vessel-map {
  width: 100%;
  height: 100%;

  #vesselVoyageHistoryMap {
    width: 100%;
    height: 100%;
    position: relative;

    .player {
      position: absolute;
      left: 2rem;
      bottom: 2rem;
    }
  }

  #vessel-history-camera-are {
    position: absolute;
    top: 1rem;
    right: 1rem;
    min-width: 300px;

    .history-camera-component {
      border: 2px solid white;
    }
  }

  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
    margin: 0 10px 40px 0 !important;
  }

  #vessel-style-change {
    margin-bottom: 10px !important;
  }
}
</style>
